'use client';

import cn from 'classnames';
import { default as dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useMemo, useRef, useState, useEffect, useId, useCallback } from 'react';
import { IconVideoPlayButton } from '@/assets/icons/IconVideoPlayButton';
import { IconVideoPauseButton } from '@/assets/icons/IconVideoPauseButton';
import { IconReplayButton } from '@/assets/icons/IconReplayButton';
import styles from './audioPlayer.module.scss';

export interface AudioPlayerProps {
    source: string;
    name?: string;
    isActive: boolean;
    onActivate: (isActive: boolean) => void;
}

interface TimeState {
    currentTime: number;
    timeRemaining: number;
}

dayjs.extend(utc);

export function AudioPlayer({ source, name = '', isActive, onActivate }: AudioPlayerProps): React.JSX.Element {
    const audioRef = useRef<HTMLAudioElement>(null);

    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [isEnded, setIsEnded] = useState<boolean>(false);
    const [duration, setDuration] = useState(0);
    const [progressPercent, setProgressPercent] = useState(0);
    const [time, setTime] = useState<TimeState>({
        currentTime: 0,
        timeRemaining: 0,
    });
    const showPauseButton = useMemo(() => !isEnded && isPlaying, [isPlaying, isEnded]);
    const showPlayButton = useMemo(() => !isEnded && !isPlaying, [isPlaying, isEnded]);

    const inputId = useId();

    const analytics: DataLayerInfo = {
        audioInfo: {
            name: name || source.split('/').pop() || '',
        },
    };

    const formatTime = (time: number): string => {
        return dayjs.utc(time * 1000).format('m:ss');
    };

    const handleAudioEnded = () => {
        setIsEnded(true);
        setIsPlaying(false);
    };

    const handleScrub = (e: React.ChangeEvent<HTMLInputElement>) => {
        const audioElement = audioRef.current;
        const time = parseFloat(e.target.value);

        if (audioElement) {
            audioElement.currentTime = time;
        }
    };

    const handleLoadedMetadata = useCallback(() => {
        const audioElement = audioRef.current;
        const duration = audioElement?.duration || 0;

        setDuration(duration);
        setTime((prevTime) => ({
            ...prevTime,
            timeRemaining: Math.floor(duration) - prevTime.currentTime,
        }));
    }, []);

    const handleTimeUpdate = () => {
        const audioElement = audioRef.current;
        const currentTime = audioElement?.currentTime || 0;
        const progressPercent = Math.floor((currentTime / duration) * 10000) / 100;

        setTime({
            currentTime: Math.floor(currentTime),
            timeRemaining: Math.floor(duration) - Math.floor(currentTime),
        });
        setProgressPercent(progressPercent);
    };

    const handlePlayPause = () => {
        const currentPlayingState = isPlaying;
        const newPlayingState = !currentPlayingState;

        onActivate(newPlayingState);
        setIsPlaying(newPlayingState);

        if (newPlayingState) {
            setIsEnded(false);
        }
    };

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.addEventListener('pause', () => setIsPlaying(false));
        }
    }, [audioRef]);

    useEffect(() => {
        if (audioRef.current?.readyState && audioRef.current?.readyState >= 1) {
            // If metadata is cached
            handleLoadedMetadata();
        }
    }, [handleLoadedMetadata]);

    useEffect(() => {
        const audioElement = audioRef?.current;

        if (isActive && isPlaying) {
            audioElement?.play();
        } else {
            audioElement?.pause();
            setIsPlaying(false);
        }
    }, [isPlaying, isActive]);

    useEffect(() => {
        if (isPlaying) {
            const userPlayEvent = new CustomEvent('sxm-media-play', { bubbles: true });
            audioRef.current?.dispatchEvent(userPlayEvent);
        } else if (isEnded) {
            const userCompleteEvent = new CustomEvent('sxm-media-complete');
            audioRef.current?.dispatchEvent(userCompleteEvent);
        } else {
            const userCompleteEvent = new CustomEvent('sxm-media-pause');
            audioRef.current?.dispatchEvent(userCompleteEvent);
        }
    }, [isPlaying, isEnded]);

    return (
        <div className={cn(styles.audioContainer, { [styles.active]: isActive })}>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption -- Captions require a CMS-provided VTT file; audio elements are better served by a transcript alternative. */}
            <audio
                src={source}
                ref={audioRef}
                onEnded={handleAudioEnded}
                onLoadedMetadata={handleLoadedMetadata}
                onTimeUpdate={handleTimeUpdate}
                data-audioplayer={analytics.audioInfo.name}
            />
            <div className={cn(styles.controls, { [styles.active]: isActive })}>
                {showPlayButton && (
                    <button
                        onClick={handlePlayPause}
                        aria-label={`play ${name}`}
                        className={styles.playButton}
                    >
                        <IconVideoPlayButton />
                    </button>
                )}
                {showPauseButton && (
                    <button onClick={handlePlayPause} aria-label={`pause ${name}`}>
                        <IconVideoPauseButton />
                    </button>
                )}
                {isEnded && (
                    <button onClick={handlePlayPause} aria-label={`replay ${name}`}>
                        <IconReplayButton />
                    </button>
                )}

                {isActive && (
                    <div className={styles.slideAnimation}>
                        <time
                            className={cn(styles.timeEllapsed, styles.lightTextColor)}
                            aria-label={`time ellapsed ${formatTime(time.currentTime)}`}
                        >
                            {formatTime(time.currentTime)}
                        </time>
                        <div className={styles.progressBarWrapper}>
                            <input
                                type="range"
                                id={inputId}
                                min={0}
                                max={duration}
                                step="any"
                                value={audioRef.current?.currentTime}
                                onChange={handleScrub}
                                aria-valuetext="Audio bar slider"
                                aria-label="Audio bar slider"
                            />
                            <div
                                className={cn(styles.progressBar)}
                                style={{
                                    background: `linear-gradient(to right, var(--range-progress-bar-ellapsed) ${progressPercent}%, var(--range-progress-bar-remaining) ${progressPercent}%)`,
                                }}
                            />
                        </div>
                        <time
                            className={cn(styles.timeRemaining, styles.timeRemainingIsActive, styles.lightTextColor)}
                            aria-label={`time remaining ${formatTime(time.timeRemaining)}`}
                        >
                            {formatTime(time.timeRemaining)}
                        </time>
                    </div>
                )}

                {!isActive && (
                    <time
                        className={cn(styles.timeRemaining, styles.timeRemainingNotActive)}
                        aria-label={`audio duration ${formatTime(duration)}`}
                    >
                        {formatTime(duration)}
                    </time>
                )}
            </div>
        </div>
    );
}
